<template>
    <v-dialog v-model="internalDialog" max-width="1400" persistent scrollable :z-index="100">
        <v-card style="height: 90vh; display: flex; flex-direction: column;" class="pt-44">
            <v-card-title class="text-h5 d-flex align-center">
                {{ goal ? goal.name : 'Goal Details' }}
                <v-spacer></v-spacer>
                <v-btn color="error" small outlined class="mr-2" @click="confirmDeleteGoal">
                    <v-icon left small>mdi-delete</v-icon>
                    Delete
                </v-btn>
                <v-btn color="primary" small class="mr-2" @click="editGoal">
                    <v-icon left small>mdi-pencil</v-icon>
                    Edit
                </v-btn>
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-subtitle class="pb-2">
                {{ goal ? goal.description : '' }}
            </v-card-subtitle>

            <v-divider></v-divider>

            <v-card-text class="pa-6" style="flex: 1 1 auto; overflow-y: auto;">
                <div class="d-flex align-center mb-6">
                    <v-tabs v-model="activeTab" class="milestone-tabs flex-grow-1">
                        <v-tab v-for="category in milestoneCategories" :key="category">
                            {{ category }} ({{ getMilestoneCategoryCount(category) }})
                        </v-tab>
                    </v-tabs>
                    
                    <div class="ml-4">
                        <template v-if="!isAddingTeam">
                            <v-btn color="primary" small outlined @click="startAddingTeam">
                                + Add Team
                            </v-btn>
                        </template>
                        <template v-else>
                            <div class="d-flex align-center">
                                <v-select
                                    v-model="selectedTeamToAdd"
                                    :items="availableTeams"
                                    label="Select a team..."
                                    dense
                                    outlined
                                    hide-details
                                    style="min-width: 200px;"
                                    class="mr-2"
                                ></v-select>
                                <v-btn small color="primary" @click="confirmAddTeam" :disabled="!selectedTeamToAdd">
                                    Add
                                </v-btn>
                                <v-btn small text @click="cancelAddTeam" class="ml-2">
                                    Cancel
                                </v-btn>
                            </div>
                        </template>
                    </div>
                </div>

                <v-tabs-items v-model="activeTab">
                    <v-tab-item v-for="category in milestoneCategories" :key="category">
                        <div class="milestone-section">
                            <div class="section-header mb-4">
                                <h3 class="text-h6">{{ category }}</h3>
                                <v-btn color="primary" small outlined @click="addMilestone(category)">
                                    <v-icon left small>mdi-plus</v-icon>
                                    Add Milestone
                                </v-btn>
                            </div>

                            <!-- Milestone Table -->
                            <UnifiedTreeViewDashboard
                                :tasks="getMilestonesByCategory(category)"
                                :users="users()"
                                :companies="[]"
                                task-type="milestone"
                                :can-edit-tasks="true"
                                :container-height="800"
                                :goal-id="goal ? goal.id : null"
                                :company-id="goal ? goal.company_id : null"
                                :role-id="getRoleIdByName(category)"
                                @optimisticUpdate="handleMilestoneUpdate"
                            />
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
        </v-card>

        <!-- Task Creation Dialog for Milestones -->
        <TaskCreationDialog
            v-model="createMilestoneDialog"
            :users="users()"
            :companies="companies()"
            :goals="goals ? goals() : []"
            :default-goal-id="goal ? goal.id : null"
            :default-company-id="goal ? goal.company_id : null"
            :default-role-id="getRoleIdByName(selectedCategory)"
            :task-type="'milestone'"
            @tasks-created="onMilestoneCreated"
        />
    </v-dialog>
</template>

<script>
import UnifiedTreeViewDashboard from '../../modules/UnifiedTreeViewDashboard.vue';
import TaskCreationDialog from '../../modules/TaskCreationDialog.vue';

export default {
    name: 'ViewGoalDialog',
    components: {
        UnifiedTreeViewDashboard,
        TaskCreationDialog
    },
    inject: ['users', 'companies', 'goals', 'optimisticUpdateGoal', 'optimisticDeleteGoal'],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        goal: {
            type: Object,
            default: null
        },
        initialTeamFilter: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            activeTab: 0,
            loading: false,
            milestoneCategories: [],
            milestones: [],
            isAddingTeam: false,
            selectedTeamToAdd: null,
            allRoles: [],
            allRolesData: [], // Store full role objects with IDs
            createMilestoneDialog: false,
            selectedCategory: null,
            deleteConfirmDialog: false,
            deletingGoal: false,
            roleOptions: ['None'],
            priorityOptions: ['Low', 'Medium', 'High'],
            statusOptions: ['To Do', 'In Progress', 'Done']
        };
    },
    computed: {
        internalDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        usersList() {
            const users = this.users();
            return [{ id: null, name: 'Unassigned' }, ...users];
        },
        availableTeams() {
            // Return roles that are not already in milestoneCategories
            return this.allRoles.filter(role => !this.milestoneCategories.includes(role));
        }
    },
    watch: {
        async value(newVal) {
            if (newVal && this.goal) {
                // Load roles first if not already loaded
                if (this.allRolesData.length === 0) {
                    await this.getUserRoles();
                } else {
                    // Initialize milestone categories from goal's roles_included
                    this.initializeMilestoneCategories();
                }
                // Load milestones after roles are ready
                await this.loadMilestones();
                
                // If initialTeamFilter is provided, switch to that team's tab
                if (this.initialTeamFilter && this.milestoneCategories.includes(this.initialTeamFilter)) {
                    const teamIndex = this.milestoneCategories.indexOf(this.initialTeamFilter);
                    this.activeTab = teamIndex;
                }
            }
        }
    },
    mounted() {
        this.getUserRoles();
    },
    methods: {
        initializeMilestoneCategories() {
            // Start with default teams
            const defaultTeams = [];
            if (this.allRoles.includes('Marketing')) defaultTeams.push('Marketing');
            if (this.allRoles.includes('Product Developers')) defaultTeams.push('Product Developers');
            
            // Add additional saved roles from roles_included that aren't already in defaults
            if (this.goal && this.goal.roles_included && Array.isArray(this.goal.roles_included) && this.goal.roles_included.length > 0) {
                const savedRoleNames = this.goal.roles_included
                    .map(roleId => this.getRoleNameById(parseInt(roleId)))
                    .filter(name => name !== null && !defaultTeams.includes(name));
                
                this.milestoneCategories = [...defaultTeams, ...savedRoleNames];
            } else {
                // Use just the defaults if no saved roles
                this.milestoneCategories = defaultTeams.length > 0 ? defaultTeams : this.allRoles.slice(0, 2);
            }
        },
        async getUserRoles() {
            try {
                const response = await this.$axios.get('api/users/get_user_roles');
                if (response.data && response.data.response) {
                    const roles = response.data.data || [];
                    // Store full role objects with IDs
                    this.allRolesData = roles;
                    const roleNames = roles.map(role => role.name);
                    // Store all roles for reference
                    this.allRoles = roleNames;
                    
                    // Initialize milestone categories from goal's roles_included
                    this.initializeMilestoneCategories();
                    
                    // Use role names for role options dropdown
                    this.roleOptions = ['None', ...roleNames];
                    
                    // Note: Milestones will be loaded by the watcher after this completes
                }
            } catch (error) {
                console.error('Error fetching user roles:', error);
                this.$toast.error('Failed to load user roles');
            }
        },
        closeDialog() {
            this.internalDialog = false;
        },
        editGoal() {
            this.$emit('edit-goal', this.goal);
            this.closeDialog();
        },
        confirmDeleteGoal() {
            if (confirm('Are you sure you want to delete this goal? This will also delete all associated milestones.')) {
                this.deleteGoal();
            }
        },
        async deleteGoal() {
            if (!this.goal || !this.goal.id) return;
            
            this.deletingGoal = true;
            try {
                await this.optimisticDeleteGoal(
                    this.goal.id,
                    async () => {
                        const response = await this.$axios.delete(`api/goals-v2/${this.goal.id}`);
                        if (!response.data.response) {
                            throw new Error(response.data.message || 'Failed to delete goal');
                        }
                        return response.data;
                    }
                );
                
                this.$toast.success('Goal deleted successfully');
                this.$emit('goal-deleted', this.goal.id);
                this.closeDialog();
            } catch (error) {
                console.error('Error deleting goal:', error);
                this.$toast.error(error.message || 'Failed to delete goal');
            } finally {
                this.deletingGoal = false;
            }
        },
        async loadMilestones() {
            if (!this.goal || !this.goal.id) return;
            
            this.loading = true;
            try {
                const response = await this.$axios.get(`api/goals-v2/get_goal_milestones/${this.goal.id}`);
                console.log('Goal Milestones API Response:', response.data);
                
                if (response.data && response.data.response) {
                    const goalTasks = response.data.data || [];
                    console.log('Goal milestones loaded:', goalTasks.length, 'for goal ID:', this.goal.id);
                    
                    // Map tasks to milestones with category based on role name
                    this.milestones = goalTasks.map(task => {
                        // Get role name from task's role relationship or role_id
                        const roleName = task.role?.name || this.getRoleNameById(task.role_id) || 'Unassigned';
                        
                        return {
                            ...task,
                            category: roleName
                        };
                    });
                    console.log('Mapped milestones:', this.milestones);
                }
            } catch (error) {
                console.error('Error loading milestones:', error);
                this.$toast.error('Failed to load milestones');
            } finally {
                this.loading = false;
            }
        },
        getRoleNameById(roleId) {
            if (!roleId) return null;
            const role = this.allRolesData.find(r => r.id === roleId);
            return role ? role.name : null;
        },
        getMilestonesByCategory(category) {
            return this.milestones.filter(m => m.category === category);
        },
        getMilestoneCategoryCount(category) {
            return this.getMilestonesByCategory(category).length;
        },
        addMilestone(category) {
            this.selectedCategory = category;
            this.createMilestoneDialog = true;
        },
        async onMilestoneCreated() {
            this.createMilestoneDialog = false;
            // Reload milestones from API to get the latest data
            await this.loadMilestones();
            // Emit goal-updated event to notify parent component
            this.$emit('goal-updated');
        },
        async handleMilestoneUpdate(event) {
            // Reload milestones when tasks are created, updated, or replaced
            if (event.type === 'create' || event.type === 'replace' || event.type === 'update') {
                await this.loadMilestones();
                
                // Emit goal-updated event to notify parent component to recalculate progress
                this.$emit('goal-updated');
            }
        },
        startAddingTeam() {
            this.selectedTeamToAdd = null;
            this.isAddingTeam = true;
        },
        getRoleIdByName(roleName) {
            if (!roleName) return null;
            const role = this.allRolesData.find(r => r.name === roleName);
            return role ? role.id : null;
        },
        cancelAddTeam() {
            this.isAddingTeam = false;
            this.selectedTeamToAdd = null;
        },
        async confirmAddTeam() {
            if (this.selectedTeamToAdd && !this.milestoneCategories.includes(this.selectedTeamToAdd)) {
                // Update goal's roles_included array via API
                const roleId = this.getRoleIdByName(this.selectedTeamToAdd);
                if (roleId && this.goal) {
                    try {
                        // Get current roles_included or initialize as empty array
                        const currentRoles = this.goal.roles_included || [];
                        
                        // Append the new role ID as string to the array
                        const updatedRoles = [...currentRoles, String(roleId)];
                        
                        // Update the goal via optimistic update
                        if (this.optimisticUpdateGoal) {
                            await this.optimisticUpdateGoal(
                                this.goal.id,
                                { roles_included: updatedRoles },
                                async () => {
                                    const response = await this.$axios.post(`api/goals-v2/${this.goal.id}`, {
                                        name: this.goal.name,
                                        description: this.goal.description,
                                        target_date: this.goal.target_date,
                                        company_id: this.goal.company_id,
                                        roles_included: updatedRoles
                                    });
                                    if (!response.data.response) {
                                        throw new Error(response.data.message || 'Failed to update goal');
                                    }
                                    return response.data;
                                }
                            );
                        }
                        
                        // Reinitialize categories from updated goal data (which now includes the new role)
                        this.initializeMilestoneCategories();
                        
                        // Reload milestones to ensure they appear in the new tab
                        await this.loadMilestones();
                        
                        this.$toast.success(`${this.selectedTeamToAdd} team added to goal`);
                    } catch (error) {
                        console.error('Error updating goal with new team:', error);
                        this.$toast.error('Failed to add team to goal');
                    }
                }
                
                this.isAddingTeam = false;
                this.selectedTeamToAdd = null;
            }
        },
        toggleMilestoneComplete(milestone) {
            // TODO: Update milestone completion status via API
            this.updateMilestone(milestone);
        },
        updateMilestone(milestone) {
            // TODO: Update milestone via API
            console.log('Updating milestone:', milestone);
        },
        getPriorityColor(priority) {
            const colors = {
                'Low': 'grey',
                'Medium': 'orange',
                'High': 'red'
            };
            return colors[priority] || 'grey';
        },
        getStatusColor(status) {
            const colors = {
                'To Do': 'blue',
                'In Progress': 'orange',
                'Done': 'green'
            };
            return colors[status] || 'blue';
        }
    }
};
</script>

<style scoped>
.milestone-tabs {
    border-bottom: 1px solid #e0e0e0;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>


